import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';

import { PhotoIcon } from '@heroicons/react/24/solid';
import { Modal } from '../../../../components/modal/Modal';
import { Button } from '../../../../components/buttons/Button';
import { InputTextArea } from '../../../../components/inputTextArea/InputTextArea';
import { InputText } from '../../../../components/inputs/InputText';
import { validUrl } from '../../../../common/urlValid';
import { createNewPost, getUserPosts } from '../../../../redux/actions/home';
import { optionsTypeUser } from '../../../../static/data';
import { MultiSelect } from 'react-multi-select-component';
import { InputDateWithIcon } from '../../../../components/inputDateWithIcon/InputDateWithIcon';

export const ModalAddPost = ({ open, setOpen }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user)
    const { _id: userId } = user;
    const cancelButtonRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [typeUserSelected, setTypeUserSelected] = useState([]);
    const [releaseDateState, setReleaseDateState] = useState(Date.now());
    const [expirationDateState, setExpirationDateState] = useState(null);
    const [formData, setFormData] = useState({
        file: null,
        ext: '',
        message: '',
        link: '',
        title: '',
    });
    const rolesToAdd = ["director", "administrativo"];

    useEffect(() => {
        setFormData({
            file: null,
            ext: '',
            message: '',
            link: '',
            title: '',
        });
        setTypeUserSelected([]);
        setReleaseDateState(Date.now());
        setExpirationDateState(null);
        setLoading(false);
        setPreviewImage(null);
        setPreviewFile(null);
    }, [open]);


    const onChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const handleEditImage = () => {
        setFormData((prevData) => ({
            ...prevData,
            file: null,
            ext: '',
        }));
        setPreviewImage(null)
        setPreviewFile(null)
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        // Validar el formato del archivo
        const allowedExtensions = ['png', 'jpeg', 'jpg'];
        const fileName = file.name.toLowerCase();
        const isValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

        if (!isValidExtension) {
            toast.error('Formato de archivo no válido. Solo se permiten archivos .png, .jpeg y .jpg.');
            // Puedes agregar más lógica aquí, como restablecer el formulario o realizar otras acciones
            return;
        }

        // Validar el tamaño del archivo (en bytes)
        const maxSizeInBytes = 3 * 1024 * 1024; // 3 MB
        if (file.size > maxSizeInBytes) {
            toast.error('El tamaño del archivo no puede ser mayor a 3 MB.');
            // Puedes agregar más lógica aquí, como restablecer el formulario o realizar otras acciones
            return;
        }

        // Obtener la extensión del archivo
        const fileExtension = fileName.split('.').pop();

        // Continuar con el manejo del archivo si tanto el formato como el tamaño son válidos
        setFormData((prevData) => ({
            ...prevData,
            file: file,
            ext: fileExtension,
        }));

        const reader = new FileReader();

        reader.onloadend = () => {
            if (fileExtension === 'pdf' || fileExtension === 'docx') {
                setPreviewFile(fileName);
            } else {
                setPreviewImage(reader.result);
            }
        };

        if (file) {
            reader.readAsDataURL(file);
        } else {
            setPreviewImage(null);
        }
    };

    const validateForm = () => {
        if (formData.link) {
            if (!validUrl(formData.link)) {
                toast.error('Ingresa un enlance url valido');
                return false;
            }
        }
        if (!formData.title) {
            toast.error('Ingresa un titulo para tu publicación');
            return false;
        }
        if (!formData.message) {
            toast.error('Ingresa un mensaje para tu publicación');
            return false;
        }
        return true;
    }

    const handleAddMessageUrl = async () => {
        const valid = validateForm();
        if (valid) {
            setLoading(true);
            let groupOfUser = typeUserSelected.length > 0 ? typeUserSelected.map(typeUser => typeUser.value) : undefined;

            rolesToAdd.forEach(role => {
                if (!groupOfUser.includes(role)) {
                    groupOfUser.push(role);
                }
            });

            const releaseDate = releaseDateState ? releaseDateState : undefined;
            const expirationDate = expirationDateState ? expirationDateState : undefined;
            dispatch(createNewPost(formData.file, userId, formData.message, formData.link, formData.title, groupOfUser, releaseDate, expirationDate))
                .then((result) => {
                    if (result.status === 201) {
                        setOpen(false);
                        setFormData({
                            file: null,
                            publicId: '',
                            ext: '',
                            message: '',
                            link: '',
                            title: '',
                        });
                        setTypeUserSelected([]);
                        setReleaseDateState(Date.now());
                        setExpirationDateState(null);
                        setLoading(false);
                        setPreviewImage(null)
                        setPreviewFile(null)
                        setLoading(false);
                        dispatch(getUserPosts(userId));
                    } else {
                        toast.error(result.message);
                        setOpen(false);
                        setFormData({
                            file: null,
                            publicId: '',
                            ext: '',
                            message: '',
                            link: '',
                            title: '',
                        });
                        setTypeUserSelected([]);
                        setReleaseDateState(Date.now());
                        setExpirationDateState(null);
                        setLoading(false);
                        setPreviewImage(null)
                        setPreviewFile(null)
                        setLoading(false);
                    }
                });
        }
    }


    return (
        <Modal open={open} setOpen={setOpen} cancelButtonRef={cancelButtonRef}>
            <div className="bg-white p-4">
                <div className="flex flex-col">

                    <Dialog.Title as="h3" className="text-center text-lg font-semibold leading-6 text-gray-950">
                        Crear una publicación
                    </Dialog.Title>
                    <div className="mt-6">
                        <p className="text-sm text-center text-gray-600">
                            Aqui puedes crear una publicación con una imagen, link o mensaje, selecciona una imagen y escribe un mensaje.
                        </p>

                    </div>
                    <div className='w-[90%] mx-auto grid grid-cols-1 gap-4 m-8'>
                        <div>
                            {(!previewImage && !previewFile) && (
                                <div className='flex justify-center rounded-lg border border-dashed px-6 py-10 mt-2'>
                                    <div className="text-center">
                                        <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                                        <div className="mt-4 flex flex-wrap justify-center gap-1 text-sm leading-6 text-gray-600">
                                            <label
                                                htmlFor="file-upload"
                                                className="relative cursor-pointer rounded-mdbg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                            >
                                                <span>Seleciona una imagen</span>
                                                <input
                                                    id="file-upload"
                                                    name="file-upload"
                                                    type="file"
                                                    className="sr-only"
                                                    onChange={handleFileChange}
                                                />
                                            </label>
                                            <p className="text-xs leading-5 text-gray-700">Tipos de imagen permitidos .png, .jpeg y .jpg.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {previewImage && (
                                <div className=''>
                                    <img
                                        src={previewImage}
                                        alt="Encabezado"
                                        className="mt-4 mx-auto  w-11/12"
                                    />
                                    <div className="mt-6 flex items-center justify-around gap-x-6">
                                        <Button type="button" variant="primary" onClick={handleEditImage}>
                                            Selecionar otra imagen
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {previewFile && (
                                <div className=''>
                                    <img
                                        src={'https://res.cloudinary.com/dax0v05jz/image/upload/v1709457834/uploads/qlhxgtbnn9q9yujymp3l.png'}
                                        alt="Encabezado"
                                        className="mt-4 mx-auto  w-10/12"
                                    />
                                    <p className="text-md text-center font-semibold leading-5 text-gray-700">{previewFile}</p>
                                    <div className="mt-6 flex items-center justify-around gap-x-6">
                                        <Button type="button" variant="primary" onClick={handleEditImage}>
                                            Selecionar otro archivo
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='mt-4'>
                            <InputDateWithIcon
                                id={'releaseDate'}
                                label={'Fecha de publicacion'}
                                onChange={setReleaseDateState}
                                value={releaseDateState}
                                placeholder={'Ingresa la fecha de publicación'}
                                beforeToDay={true}
                            />
                        </div>
                        <div className='mt-4'>
                            <InputDateWithIcon
                                id={'expirationDate'}
                                label={'Fecha de expiración de la publicacion'}
                                onChange={setExpirationDateState}
                                value={expirationDateState}
                                placeholder={'Ingresa la fecha de expiración'}
                                beforeToDay={false}
                            />
                        </div>
                        <div className='mt-4'>
                            <h3 className="text-md font-semibold text-gray-900">Seleciona que roles pueden ver la publicacion</h3>
                            <MultiSelect
                                className='capitalize text-sm mt-2'
                                options={optionsTypeUser}
                                value={typeUserSelected}
                                onChange={setTypeUserSelected}
                                overrideStrings={{
                                    selectSomeItems: "selecciona roles",
                                    selectAll: "Seleciona todos",
                                    allItemsAreSelected: "Todos los elementos están seleccionados",
                                    clearSearch: "Limpiar búsqueda",
                                    search: "Buscar",
                                    noOptions: "Sin opciones"
                                }}
                            />
                        </div>
                        <div className='mt-4'>
                            <InputText
                                id={'title'}
                                name={'title'}
                                type={'text'}
                                label={'Ingresa tu titulo de tu publicación'}
                                onChange={(e) => onChange(e)}
                                value={formData.title}
                                placeholder={'Ingresa tu titulo'}
                            />
                        </div>
                        <div>
                            <InputTextArea
                                id={'message'}
                                name={'message'}
                                type={'text'}
                                label={'Ingresa tu mensaje a publicar'}
                                onChange={(e) => onChange(e)}
                                value={formData.message}
                                placeholder={'Ingresa tu mensaje'}
                                rows={4}
                            />
                        </div>
                        <div>
                            <InputText
                                id={'link'}
                                name={'link'}
                                type={'text'}
                                label={'Ingresa tu link a publicar'}
                                onChange={(e) => onChange(e)}
                                value={formData.link}
                                placeholder={'Ingresa tu link'}
                            />
                        </div>
                        <div className='mt-[20px]'>
                            <Button
                                type="button"
                                variant="primary"
                                fullWidth
                                loading={loading}
                                onClick={() => handleAddMessageUrl()}
                            >
                                Publicar mensaje
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
